import { useState, useEffect, useCallback } from 'react'
import { usersApi } from '../../api/users'
import { chatsApi } from '../../api/chats'
import { useChatStore } from '../../stores/chatStore'
import { useUiStore } from '../../stores/uiStore'
import { Avatar } from '../ui/Avatar'
import type { User } from '../../types'

interface Props {
  onClose: () => void
  onOpenChat: () => void
}

export function ContactsPanel({ onClose, onOpenChat }: Props) {
  const [contacts, setContacts] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState<User[]>([])
  const [searching, setSearching] = useState(false)
  const [addingId, setAddingId] = useState<string | null>(null)
  const { addToast } = useUiStore()

  useEffect(() => {
    usersApi.listContacts()
      .then((r) => setContacts(r.contacts ?? []))
      .catch(() => addToast('error', 'Не удалось загрузить контакты'))
      .finally(() => setLoading(false))
  }, [addToast])

  const handleSearch = useCallback(async (q: string) => {
    setSearch(q)
    if (!q.trim()) { setSearchResults([]); return }
    setSearching(true)
    try {
      const r = await usersApi.search(q)
      setSearchResults(r.users ?? [])
    } catch { /* ignore */ }
    finally { setSearching(false) }
  }, [])

  const handleAddContact = async (user: User) => {
    setAddingId(user.id)
    try {
      await usersApi.addContact(user.id)
      setContacts((prev) => prev.some((c) => c.id === user.id) ? prev : [...prev, user])
      addToast('success', `${user.first_name || user.username} added to contacts`)
    } catch {
      addToast('error', 'Не удалось добавить контакт')
    } finally {
      setAddingId(null)
    }
  }

  const handleRemoveContact = async (userId: string) => {
    try {
      await usersApi.removeContact(userId)
      setContacts((prev) => prev.filter((c) => c.id !== userId))
    } catch {
      addToast('error', 'Не удалось удалить контакт')
    }
  }

  const handleOpenChat = async (user: User) => {
    try {
      const chat = await chatsApi.getOrCreate(user.id)
      useChatStore.getState().upsertChat({ ...chat, peer: user })
      useChatStore.getState().setActiveChat(chat.id)
      onClose()
      onOpenChat()
    } catch {
      addToast('error', 'Не удалось открыть чат')
    }
  }

  const visibleContacts = search.trim()
    ? contacts.filter((c) =>
        [c.first_name, c.last_name, c.username].join(' ').toLowerCase().includes(search.toLowerCase())
      )
    : contacts

  const displaySearch = search.trim().length > 0
  const newUsers = displaySearch
    ? searchResults.filter((u) => !contacts.some((c) => c.id === u.id))
    : []

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onClose} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">Контакты</h2>
        <span className="text-txt-meta text-xs">{contacts.length}</span>
      </div>

      {/* Search bar */}
      <div className="px-4 py-2 border-b border-divider">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-txt-meta w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Поиск людей…"
            className="w-full bg-bg-input rounded-xl pl-9 pr-3 py-2 text-sm text-txt-primary outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Your contacts */}
            {visibleContacts.length > 0 && (
              <div>
                <p className="text-txt-meta text-xs uppercase tracking-wider font-medium px-4 py-2">
                  {displaySearch ? 'Контакты' : 'Все контакты'}
                </p>
                {visibleContacts.map((user) => (
                  <ContactRow
                    key={user.id}
                    user={user}
                    action="chat"
                    onAction={() => handleOpenChat(user)}
                    onRemove={() => handleRemoveContact(user.id)}
                  />
                ))}
              </div>
            )}

            {/* Global search results (not yet a contact) */}
            {newUsers.length > 0 && (
              <div>
                <p className="text-txt-meta text-xs uppercase tracking-wider font-medium px-4 py-2">
                  People
                </p>
                {newUsers.map((user) => (
                  <ContactRow
                    key={user.id}
                    user={user}
                    action="add"
                    adding={addingId === user.id}
                    onAction={() => handleAddContact(user)}
                  />
                ))}
              </div>
            )}

            {searching && (
              <div className="flex justify-center py-4">
                <div className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
              </div>
            )}

            {!loading && !searching && contacts.length === 0 && !search && (
              <div className="text-center py-12 px-4">
                <p className="text-5xl mb-3">👥</p>
                <p className="text-txt-primary font-medium mb-1">Нет контактов</p>
                <p className="text-txt-meta text-sm">Найдите людей и добавьте их в контакты</p>
              </div>
            )}

            {!loading && displaySearch && visibleContacts.length === 0 && newUsers.length === 0 && !searching && (
              <div className="text-center py-8 text-txt-meta text-sm">No results for "{search}"</div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function ContactRow({
  user,
  action,
  adding = false,
  onAction,
  onRemove,
}: {
  user: User
  action: 'chat' | 'add'
  adding?: boolean
  onAction: () => void
  onRemove?: () => void
}) {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || 'User'

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-bg-hover transition-colors group">
      <button onClick={onAction} className="flex items-center gap-3 flex-1 min-w-0">
        <Avatar name={name} src={user.avatar_url} size={44} online={user.online} />
        <div className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-1">
            <span className="text-txt-primary text-[15px] font-medium truncate">{name}</span>
            {user.is_verified && <span className="text-accent text-xs">✓</span>}
          </div>
          <span className="text-txt-meta text-xs truncate">
            {user.username ? `@${user.username}` : user.online ? 'online' : 'offline'}
          </span>
        </div>
      </button>
      {action === 'add' ? (
        <button
          onClick={onAction}
          disabled={adding}
          className="text-accent text-sm font-medium shrink-0 disabled:opacity-40 hover:opacity-80 transition-opacity"
        >
          {adding ? '…' : 'Добавить'}
        </button>
      ) : (
        onRemove && (
          <button
            onClick={(e) => { e.stopPropagation(); onRemove() }}
            className="text-txt-meta hover:text-red-400 transition-colors shrink-0 opacity-0 group-hover:opacity-100 text-sm"
            title="Удалить контакт"
          >
            ✕
          </button>
        )
      )}
    </div>
  )
}
